/**
 * NavNode class represents a node in a navigation tree.
 * This class manages a tree node with bidirectional relationships.
 * Each node has a parent and can have multiple children.
 * Additionally, it can store title, href and metadata.
 *
 */
export default class NavNode {
    /**
     * @param {Object} options - Constructor options.
     * @param {NavNode| null} [options.parent=null] - Parent node.
     * @param {string} [options.title=''] - Title of the node.
     * @param {string} [options.subtitle=''] - Subtitle.
     * @param {string} [options.short_subtitle=''] - Short subtitle.
     * @param {string} [options.href=''] - Hyperlink reference.
     * @param {string} [options.entry_type='default'] - Type of the entry (e.g., 'default', 'model-range', 'automation', 'machining-center').
     * @param {boolean} [options.show_teaser=false]
     * @param {string} [options.teaser_text='']
     * @param {string} [options.teaser_image='']
     * @param {string} [options.image_alt='']
     * @param {string} [options.image_title='']
     * @param {string} [options.button_title='']
     * @param {boolean} [options.show_subtitle_in_link=false]
     * @param {boolean} [options.wide_text=false]
     * @param {string} [options.second_button_title='']
     * @param {string} [options.second_button_href='']
     * @param {Array} [options.datasheet=[]] - List of datasheets for entry_type automation.
     */
    constructor({
        parent = null,
        title = '',
        subtitle = '',
        short_subtitle = '',
        href = '',
        entry_type = 'default',
        show_teaser = false,
        teaser_text = '',
        teaser_image = '',
        image_alt = '',
        image_title = '',
        button_title = '',
        show_subtitle_in_link = false,
        wide_text = false,
        second_button_title = '',
        second_button_href = '',
        datasheet = [],
    } = {}) {
        this._parent = parent
        this.children = []
        this.title = title
        this.subtitle = subtitle
        this.short_subtitle = short_subtitle
        this.href = href
        this.entry_type = entry_type
        this.isActive = false
        this.show_teaser = show_teaser
        this.teaser_text = teaser_text
        this.teaser_image = teaser_image
        this.image_alt = image_alt
        this.image_title = image_title
        this.button_title = button_title
        this.show_subtitle_in_link = show_subtitle_in_link
        this.wide_text = wide_text
        this.second_button_title = second_button_title
        this.second_button_href = second_button_href
        this.datasheet = datasheet
    }

    get parent() {
        return this._parent
    }

    set parent(parent) {
        // Remove from old parent's children
        if (this._parent) {
            const index = this._parent.children.indexOf(this)
            if (index !== -1) {
                this._parent.children.splice(index, 1)
            }
        }

        // Set new parent
        this._parent = parent

        // Add to new parent's children
        if (parent && !parent.children.includes(this)) {
            parent.children.push(this)
        }

        // eslint-disable-next-line no-setter-return
        return this
    }

    // Add a child node
    addNode(props = {}) {
        const newNode = new NavNode({ ...props, parent: this })
        this.children.push(newNode)
        return newNode
    }
}
